import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { Divider, Toast, useToast } from '@zillow/constellation-10';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import { isSigningCompleteModalOpenState } from '../../../atoms';
import TenantSigningCompleteModal from '../LandlordLeaseView/ListView/LeaseSignPreview/TenantSigningCompleteModal';

import Disclaimer from '../../components/Widgets/Disclaimer';
import { LeaseAction, Property } from '../../models';
import { useLeasingUrlParams, useTenant } from '../../hooks';
import {
  useDisclaimerAcceptanceForUserQuery,
  useLeaseQuery,
  useLeaseSummaryQuery,
  usePropertyInfoQuery,
  useSetAttachedLeaseAcceptanceForUserMutation,
  useSetDisclaimerAcceptanceForUserMutation,
} from '../../utils/queries';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import SummaryTitle from '../../components/LeaseSummary/SummaryTitle';
import { LEASE_SUMMARY_CHECKBOX_LABELS } from '../TenantDashboard/constants';
import LeaseSummary from '../../components/LeaseSummary';
import { SUMMARY_VIEW_SUBTYPES } from '../../models/Lease';
import { STRING_CONSTANTS } from '../LandlordLeaseView/SelfServeView/constants';
import DocumentPreviewSelector from '../../components/DocumentPreviewSelector';
import LeaseAttachedCheckbox from '../../components/Widgets/LeaseAttachedCheckbox';

function TenantSelfServeView() {
  const history = useHistory();
  const { leaseId } = useLeasingUrlParams();
  const { tenant } = useTenant();
  const { enqueueToast } = useToast();

  // Get query params for nested view
  const queryParams = new URLSearchParams(window.location.search);
  const nestedViewType = queryParams.get('nestedView');

  // React Query hooks
  const leaseQuery = useLeaseQuery(leaseId, { enabled: !!leaseId });
  const summaryQueryData = useLeaseSummaryQuery(leaseId, LeaseArtifactType.LEASE, {
    enabled: !!leaseId,
  });
  const propertyQuery = usePropertyInfoQuery(leaseQuery.data?.listingAlias || '', !!leaseQuery.data?.listingAlias);
  const disclaimerAcceptanceQuery = useDisclaimerAcceptanceForUserQuery(leaseId);

  const setDisclaimerAcceptanceMutation = useSetDisclaimerAcceptanceForUserMutation();
  const setAttachedLeaseAcceptanceMutation = useSetAttachedLeaseAcceptanceForUserMutation();

  // Data from React Query
  const lease = leaseQuery.data;
  const property = propertyQuery.data ? new Property(propertyQuery.data) : null;
  const summaryData = summaryQueryData.data;
  const summaryText = summaryData?.summaryText || '';
  const summaryActions =
    summaryData?.summaryActions?.map((params: any) => new LeaseAction(params.name, params.disabled)) || [];
  const acceptedDisclaimer = disclaimerAcceptanceQuery.isLoading
    ? undefined
    : (disclaimerAcceptanceQuery.data?.acceptedDisclaimer ?? false);

  const [canClickCta, setCanClickCta] = useState(false);
  const [acceptedAttachedLeaseCheckbox, setAcceptedAttachedLeaseCheckbox] = useState(false);
  const [isAcceptingDisclaimer, setIsAcceptingDisclaimer] = useState(false);

  const [isSigningCompleteModalOpen, setIsSigningCompleteModalOpen] = useRecoilState(isSigningCompleteModalOpenState);

  useEffect(() => {
    setIsSigningCompleteModalOpen(false);
  }, [setIsSigningCompleteModalOpen]);

  function onDocumentClose() {
    if (!lease) return;
    history.push(lease.getSelfServePath());
  }

  function onDocumentError(notification: string) {
    onDocumentClose();
    enqueueToast(<Toast tone="critical">{notification}</Toast>);
  }

  function onToggleCanClickCta() {
    setCanClickCta(!canClickCta);
  }

  async function onCtaClicked() {
    if (!lease || isAcceptingDisclaimer) return;

    setIsAcceptingDisclaimer(true);

    const promises: Array<Promise<unknown>> = [];

    if (acceptedAttachedLeaseCheckbox) {
      promises.push(setAttachedLeaseAcceptanceMutation.mutateAsync({ postData: { leaseId: lease.id } }));
    }

    if (canClickCta) {
      promises.push(setDisclaimerAcceptanceMutation.mutateAsync({ postData: { leaseId: lease.id } }));
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }

    setIsAcceptingDisclaimer(false);
  }

  function renderLeaseActions(disabled?: boolean) {
    return summaryActions ? (
      <LeaseActionButtonGroup
        leaseActions={summaryActions}
        fluid
        lease={lease}
        property={property}
        disabled={disabled || isAcceptingDisclaimer}
        data-test="lease-summary-action-button"
        onPrimaryActionClick={acceptedDisclaimer ? null : onCtaClicked}
      />
    ) : null;
  }

  function onToggleAttachedLeaseAccepted() {
    setAcceptedAttachedLeaseCheckbox(!acceptedAttachedLeaseCheckbox);
  }

  function renderDisclaimer() {
    return (
      <Disclaimer
        afterCheckboxContent={
          <>
            <LeaseAttachedCheckbox checked={acceptedAttachedLeaseCheckbox} onClick={onToggleAttachedLeaseAccepted} />
            {renderLeaseActions(!canClickCta)}
          </>
        }
        label="I agree to these terms before viewing or downloading"
        isChecked={canClickCta}
        onClick={onToggleCanClickCta}
        checkboxLabel={LEASE_SUMMARY_CHECKBOX_LABELS.SELF_SERVE.WITH_NO_DOCUMENTS}
      />
    );
  }

  function renderHeader() {
    const propertyAddress = property?.streetAddress;
    const personFirstName = tenant?.firstName;
    const leaseStartDate = lease?.getFriendlyStartDate();
    const leaseType = lease?.leaseType;

    return (
      <>
        <SummaryTitle {...{ summaryText, propertyAddress, personFirstName, leaseStartDate, leaseType }} />
        {acceptedDisclaimer != null && (!acceptedDisclaimer ? renderDisclaimer() : renderLeaseActions(false))}
        <Divider css={{ marginTop: 'tight', marginBottom: 'tight' }} />
      </>
    );
  }

  function renderNestedView() {
    switch (nestedViewType) {
      case SUMMARY_VIEW_SUBTYPES.PREVIEW:
        return (
          <DocumentPreviewSelector
            leaseId={leaseId}
            lease={lease}
            property={property}
            onClose={onDocumentClose}
            onError={onDocumentError}
          />
        );
      default:
        return null;
    }
  }

  function handleCloseSigningModal() {
    setIsSigningCompleteModalOpen(false);
  }

  return (
    <>
      {lease && (
        <TenantSigningCompleteModal
          isOpen={isSigningCompleteModalOpen}
          lease={lease}
          onClose={handleCloseSigningModal}
        />
      )}
      <>
        {renderHeader()}
        <LeaseSummary
          isAlwaysExpanded
          isSelfServe
          categoryDescriptionOverrides={STRING_CONSTANTS.TENANT_CATEGORY_DESCRIPTION_OVERRIDES}
        />
        {renderNestedView()}
      </>
    </>
  );
}

export default TenantSelfServeView;
